import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Check, ChevronRight, Loader2, X, Zap } from 'lucide-react';
import workspaceApi from '../../../api/workspaceApi';
import billingApi from '../../../api/billingApi';
import { toast } from 'react-hot-toast';
import { parseDatePreservingCalendar } from '../../../utils/formatDate';
import { BillingPlan } from '../../../api/types';

const STRIPE_STANDARD_PAYMENT_LINK = process.env.REACT_APP_STRIPE_STANDARD_PAYMENT_LINK;
const STRIPE_CORE_PAYMENT_LINK = process.env.REACT_APP_STRIPE_CORE_PAYMENT_LINK;
const PENDING_STRIPE_PLAN_KEY = 'pending_stripe_plan';
const SUPPORT_EMAIL = 'support@finzcard.com';

export default function Subscribe() {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [pendingPlanStatus, setPendingPlanStatus] = useState<string | null>(
        sessionStorage.getItem(PENDING_STRIPE_PLAN_KEY)
    );
    const [isChangePlanOpen, setIsChangePlanOpen] = useState(false);
    const { activeWorkspaceId } = useWorkspace();

    const [isCancelling, setIsCancelling] = useState(false);

    // Fetch entitlement status
    const { data: entitlementRes, isLoading: isEntitlementLoading, refetch: refetchEntitlement } = useQuery({
        queryKey: ['workspaceQuota', activeWorkspaceId],
        queryFn: () => workspaceApi.getWorkspaceQuota(),
        enabled: !!activeWorkspaceId,
    });

    const { data: plansRes, isLoading: isPlansLoading } = useQuery({
        queryKey: ['billingPlans', activeWorkspaceId],
        queryFn: () => billingApi.getPlans(),
        enabled: !!activeWorkspaceId,
    });

    const { data: billingPaymentHistoryRes, isLoading: isBillingHistoryLoading } = useQuery({
        queryKey: ['billingPaymentHistory', activeWorkspaceId],
        queryFn: () => billingApi.getPaymentHistory(),
        enabled: !!activeWorkspaceId,
    });

    const planStatus = entitlementRes?.data?.entitlement?.plan_status?.toLowerCase() || 'free';
    const effectivePlanStatus = pendingPlanStatus && (isRefreshing || planStatus === 'free')
        ? pendingPlanStatus
        : planStatus;
    const isStandard = effectivePlanStatus === 'standard';
    const isCore = effectivePlanStatus === 'core' || effectivePlanStatus === 'pro' || effectivePlanStatus === 'paid';
    const isPaid = isStandard || isCore;
    const hasWorkspaceContext = Boolean(activeWorkspaceId);
    const currentPlanId = entitlementRes?.data?.entitlement?.plan_id || null;
    const currentPlanName = entitlementRes?.data?.entitlement?.plan_name || null;
    const currentStripePriceId = entitlementRes?.data?.entitlement?.stripe_price_id || null;
    const billingPlans = plansRes?.data ?? [];
    const billingHistory = billingPaymentHistoryRes?.data ?? [];

    const getFallbackPaymentLink = (plan: BillingPlan) => {
        const tier = (plan.tier || '').toLowerCase();
        return tier === 'standard' ? STRIPE_STANDARD_PAYMENT_LINK : STRIPE_CORE_PAYMENT_LINK;
    };

    const getPaymentLink = (plan: BillingPlan) => plan.stripe_payment_url || getFallbackPaymentLink(plan) || '';

    const isCustomPlan = (plan: BillingPlan) => (
        (plan.tier || '').toLowerCase() === 'custom' ||
        (plan.plan_id || '').toLowerCase() === 'custom'
    );

    const getCheckoutBlockReason = (plan: BillingPlan) => {
        if (isCustomPlan(plan)) return null;
        if (!hasWorkspaceContext) {
            return 'Select a workspace first before starting checkout.';
        }
        if (!getPaymentLink(plan)) {
            return `Stripe checkout link for ${plan.name} is not configured.`;
        }
        return null;
    };

    const getPlanLabel = (plan?: string | null) => {
        switch ((plan || '').toLowerCase()) {
            case 'standard':
                return 'Starter';
            case 'core':
            case 'pro':
            case 'paid':
                return 'Core';
            case 'free':
                return 'Free Trial';
            default:
                return plan || 'Free Trial';
        }
    };

    const isPaidStatus = (status?: string | null) => {
        const normalized = status?.toLowerCase();
        return normalized === 'standard' || normalized === 'paid' || normalized === 'pro' || normalized === 'core';
    };

    const pollEntitlementStatus = async (maxRetries = 5, delayMs = 2000) => {
        let currentStatus = 'free';
        for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
            const { data } = await refetchEntitlement();
            queryClient.invalidateQueries({ queryKey: ['workspaceQuota'] });
            const fetchedStatus = data?.data?.entitlement?.plan_status?.toLowerCase();
            if (fetchedStatus) {
                currentStatus = fetchedStatus;
            }
            if (isPaidStatus(currentStatus)) {
                return currentStatus;
            }
            if (attempt < maxRetries) {
                await new Promise((resolve) => setTimeout(resolve, delayMs));
            }
        }
        return currentStatus;
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const currentStatus = await pollEntitlementStatus();
            if (isPaidStatus(currentStatus)) {
                setPendingPlanStatus(null);
                sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
                toast.success('Subscription successfully verified and unlocked!');
            } else {
                setPendingPlanStatus(null);
                sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
                toast.error(`No active subscription found. (Status: ${currentStatus || 'unknown'})`);
            }
        } catch (error) {
            toast.error('Failed to verify subscription status. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (isPaidStatus(planStatus)) {
            setPendingPlanStatus(null);
            sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
        }
    }, [planStatus]);

    useEffect(() => {
        if (!searchParams.get('success') && !isRefreshing && planStatus === 'free' && pendingPlanStatus) {
            setPendingPlanStatus(null);
            sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
        }
    }, [searchParams, isRefreshing, planStatus, pendingPlanStatus]);

    // Cancel Subscription Mutation
    const cancelMutation = useMutation({
        mutationFn: () => billingApi.cancelSubscription(),
        onMutate: () => setIsCancelling(true),
        onSuccess: () => {
            toast.success('Your subscription has been successfully canceled.');
            queryClient.invalidateQueries({ queryKey: ['workspaceQuota'] });
            refetchEntitlement();
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || 'Failed to cancel subscription. Please contact support.';
            toast.error(msg);
        },
        onSettled: () => {
            setIsCancelling(false);
        }
    });

    // Downgrade Subscription Mutation
    const downgradeMutation = useMutation({
        mutationFn: (targetPlan: BillingPlan) => billingApi.downgradeSubscription({ target_plan: targetPlan.tier, plan_id: targetPlan.plan_id }),
        onSuccess: () => {
            toast.success('Your plan is set to downgrade at the end of the billing cycle.');
            queryClient.invalidateQueries({ queryKey: ['workspaceQuota'] });
            refetchEntitlement();
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || 'Failed to downgrade subscription. Please try again.';
            toast.error(msg);
        }
    });

    // Resume Subscription Mutation
    const resumeMutation = useMutation({
        mutationFn: () => billingApi.resumeSubscription(),
        onSuccess: () => {
            toast.success('Your subscription has been successfully resumed!');
            queryClient.invalidateQueries({ queryKey: ['workspaceQuota'] });
            refetchEntitlement();
        },
        onError: (error: any) => {
            const msg = error.response?.data?.message || 'Failed to resume subscription. Please try again.';
            toast.error(msg);
        }
    });

    const portalMutation = useMutation({
        mutationFn: () => billingApi.createPortalSession(),
        onSuccess: (response) => {
            const url = response?.data?.url;
            if (url) {
                window.location.href = url;
                return;
            }
            toast.error('Could not open the Stripe billing portal.');
        },
        onError: (error: any) => {
            const msg = error.response?.data?.detail || 'Could not open the Stripe billing portal.';
            toast.error(msg);
        }
    });

    // Upgrade Subscription Mutation
    const upgradeMutation = useMutation({
        mutationFn: (targetPlan: BillingPlan) => billingApi.upgradeSubscription({ target_plan: targetPlan.tier, plan_id: targetPlan.plan_id }),
        onSuccess: (_data, targetPlan) => {
            setPendingPlanStatus(targetPlan.tier);
            sessionStorage.setItem(PENDING_STRIPE_PLAN_KEY, targetPlan.tier);
            toast.success('🎉 Subscription upgraded successfully!', { duration: 5000 });
            queryClient.invalidateQueries({ queryKey: ['workspaceQuota'] });
            refetchEntitlement();
            handleRefresh();
        },
        onError: (error: any, variables) => {
            // If 400 or 402, redirect to Stripe Checkout since they need to enter a card
            if (error.response?.status === 400 || error.response?.status === 402) {
                toast.loading('Redirecting to secure checkout...', { duration: 3000 });
                const baseLink = getPaymentLink(variables);
                if (!baseLink) {
                    toast.error(`Missing Stripe checkout link configuration for ${variables.name}.`);
                    return;
                }
                const separator = baseLink.includes('?') ? '&' : '?';
                window.location.href = `${baseLink}${separator}client_reference_id=${activeWorkspaceId}`;
            } else {
                const msg = error.response?.data?.message || `Failed to upgrade to ${variables.name}. Please try again.`;
                toast.error(msg);
            }
        }
    });

    const handleCancel = () => {
        if (window.confirm("Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your billing cycle.")) {
            cancelMutation.mutate();
        }
    };

    // Auto-refresh on redirect from Stripe Checkout
    useEffect(() => {
        if (searchParams.get('success') === 'true') {
            const toastId = toast.loading('Syncing your payment with Stripe... Please wait a few seconds.', { duration: 10000 });
            setIsRefreshing(true);

            (async () => {
                try {
                    // Prefer the webhook-bound entitlement first; claim is only a fallback
                    // when the workspace still appears free after the webhook reconciliation window.
                    let currentStatus = await pollEntitlementStatus(3, 1500);

                    if (!isPaidStatus(currentStatus)) {
                        const claimResponse = await billingApi.claimSubscription();
                        const claimStatus = claimResponse?.data?.plan_status?.toLowerCase();
                        if (isPaidStatus(claimStatus)) {
                            currentStatus = claimStatus;
                        } else {
                            currentStatus = await pollEntitlementStatus(5, 2000);
                        }
                    }

                    if (isPaidStatus(currentStatus)) {
                        setPendingPlanStatus(null);
                        sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
                        toast.success('Subscription successfully verified and unlocked!');
                    } else {
                        setPendingPlanStatus(null);
                        sessionStorage.removeItem(PENDING_STRIPE_PLAN_KEY);
                        toast.error(`No active subscription found. (Status: ${currentStatus || 'unknown'})`);
                    }
                } catch (error) {
                    console.error('Failed to reconcile Stripe subscription after checkout redirect:', error);
                    toast.error('Failed to verify subscription status. Please try again.');
                } finally {
                    setIsRefreshing(false);
                    setTimeout(() => toast.dismiss(toastId), 10000);
                }
            })();

            // Remove 'success' param from URL after triggering so it doesn't re-trigger on reload
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('success');
            setSearchParams(newParams, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, setSearchParams]);

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return 'N/A';
        const parsed = parseDatePreservingCalendar(dateStr);
        if (!parsed) return 'N/A';
        return parsed.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatPlanPrice = (plan: BillingPlan) => {
        if (isCustomPlan(plan)) return 'Custom';
        const amount = (plan.price_cents || 0) / 100;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: (plan.currency || 'usd').toUpperCase(),
            maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
        }).format(amount);
    };

    const isPlanCurrent = (plan: BillingPlan) => {
        if (currentPlanId) return currentPlanId === plan.plan_id;
        if (currentStripePriceId) return currentStripePriceId === plan.stripe_price_id;
        return (plan.plan_id || '').toLowerCase() === effectivePlanStatus;
    };

    const isPlanDowngrade = (plan: BillingPlan) => (
        !isCustomPlan(plan) && isCore && (plan.tier || '').toLowerCase() === 'standard'
    );

    const handlePlanAction = (plan: BillingPlan) => {
        if (isCustomPlan(plan)) {
            window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Talk to Finz about a custom plan')}`;
            return;
        }
        if (isPlanCurrent(plan) && !cancelAtPeriodEnd) return;
        if (isPlanCurrent(plan) && cancelAtPeriodEnd) {
            resumeMutation.mutate();
            return;
        }
        if (isPlanDowngrade(plan)) {
            if (window.confirm(`Are you sure you want to downgrade to ${plan.name}?`)) {
                downgradeMutation.mutate(plan);
            }
            return;
        }
        if (window.confirm(`Do you want to subscribe to ${plan.name}?`)) {
            upgradeMutation.mutate(plan);
        }
    };

    const quota = entitlementRes?.data?.quota;
    const cancelAtPeriodEnd = entitlementRes?.data?.entitlement?.cancel_at_period_end;
    const currentPeriodEnd = entitlementRes?.data?.entitlement?.current_period_end;
    const upcomingPlan = entitlementRes?.data?.entitlement?.upcoming_plan?.toLowerCase();
    const deferredPlanChange = entitlementRes?.data?.entitlement?.deferred_plan_change;
    const currentBillingPlan = billingPlans.find((plan) => isPlanCurrent(plan));
    const activePlanName = currentPlanName || currentBillingPlan?.name || (
        isCore ? `${getPlanLabel('core')} Plan` : isStandard ? `${getPlanLabel('standard')} Plan` : getPlanLabel(effectivePlanStatus)
    );

    // Schedule-based downgrades stay active and do not use cancel_at_period_end.
    const isDowngradingToStandard = Boolean(deferredPlanChange && upcomingPlan === 'standard');
    const isCancellingToFree = cancelAtPeriodEnd && (upcomingPlan === 'free' || !upcomingPlan);
    const activePaidPlan = Boolean(isPaid && quota);
    const currentPeriodEndLabel = currentPeriodEnd
        ? formatDate(new Date(currentPeriodEnd * 1000).toISOString())
        : 'the end of this billing period';
    const trialEndAt = entitlementRes?.data?.entitlement?.trial_end_at;
    const parsedTrialEnd = trialEndAt ? parseDatePreservingCalendar(trialEndAt) : null;
    const isTrialExpired = Boolean(parsedTrialEnd && parsedTrialEnd.getTime() < Date.now());
    const inactivePlanContentWidthClass = billingPlans.length === 1
        ? 'max-w-[340px] sm:max-w-[400px]'
        : billingPlans.length === 2
            ? 'max-w-[704px] sm:max-w-[824px]'
            : 'max-w-full';

    const renderPlanCards = (compact = false) => {
        if (isPlansLoading) {
            return (
                <div className="flex min-h-[260px] items-center justify-center rounded-3xl border border-gray-200 bg-white">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                </div>
            );
        }

        if (billingPlans.length === 0) {
            return (
                <div className="rounded-3xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
                    No billing plans are available for this workspace yet.
                </div>
            );
        }

        const shouldFillCompactCards = compact && billingPlans.length <= 2;

        return (
            <div className="-mx-1 overflow-x-auto pb-2">
                <div className={`flex gap-6 px-1 ${shouldFillCompactCards ? 'w-full' : 'w-max'}`}>
                {billingPlans.map((plan) => {
                    const current = isPlanCurrent(plan);
                    const downgrade = isPlanDowngrade(plan);
                    const blockReason = getCheckoutBlockReason(plan);
                    const isBusy = (
                        (upgradeMutation.isPending && upgradeMutation.variables?.plan_id === plan.plan_id) ||
                        (downgradeMutation.isPending && downgradeMutation.variables?.plan_id === plan.plan_id) ||
                        (resumeMutation.isPending && current)
                    );
                    const buttonLabel = current && !cancelAtPeriodEnd
                        ? 'Current Plan'
                        : current && cancelAtPeriodEnd
                            ? 'Resume Subscription'
                            : isCustomPlan(plan)
                                ? 'Talk to Finz'
                                : downgrade
                                    ? `Downgrade to ${plan.name.replace(/\s*Plan$/i, '')}`
                                    : isStandard
                                        ? `Upgrade to ${plan.name.replace(/\s*Plan$/i, '')}`
                                        : `Start ${plan.name.replace(/\s*Plan$/i, '')}`;

                    return (
                        <div
                            key={plan.plan_id}
                            className={`relative flex flex-col rounded-2xl ${compact ? (shouldFillCompactCards ? 'min-w-[320px] flex-1 p-6' : 'w-[320px] shrink-0 p-6 sm:w-[360px]') : 'w-[340px] shrink-0 p-8 pt-10 sm:w-[400px]'} ${plan.is_recommended ? 'border-2 border-indigo-500 bg-gradient-to-b from-[#F0F0FF] to-white shadow-md' : 'border border-gray-200 bg-white shadow-sm'}`}
                        >
                            <div className="mb-4 flex items-center gap-2">
                                <h3 className={`${compact ? 'text-base' : 'text-xl'} font-bold text-gray-900`}>{plan.name}</h3>
                                {plan.is_recommended && (
                                    <span className="rounded bg-indigo-500 px-2 py-1 text-[10px] font-semibold text-white">
                                        Recommended
                                    </span>
                                )}
                                {!compact && plan.is_recommended && <Zap className="h-5 w-5 fill-amber-400 text-amber-400" />}
                            </div>
                            {plan.description && <p className="mb-4 text-sm text-gray-600">{plan.description}</p>}
                            <div className="mb-7">
                                <span className={`${compact ? 'text-2xl' : 'text-[28px]'} font-extrabold text-gray-900`}>{formatPlanPrice(plan)}</span>
                                {!isCustomPlan(plan) && <span className="ml-1 text-sm text-gray-600">/ {plan.interval || 'month'}</span>}
                            </div>

                            <ul className="mb-7 flex-1 space-y-4">
                                {(plan.features || []).map((feature, index) => {
                                    const [title, ...rest] = feature.split(':');
                                    const detail = rest.join(':').trim();
                                    return (
                                        <li key={`${plan.plan_id}-feature-${index}`} className="flex items-start gap-3">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
                                            <span className="text-sm text-gray-600">
                                                {detail ? (
                                                    <>
                                                        <span className="font-semibold text-gray-800">{title}:</span>
                                                        <br />
                                                        {detail}
                                                    </>
                                                ) : feature}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="mt-auto">
                                {!current && !compact && (
                                    <div className="mb-3 w-full text-center text-sm font-normal text-gray-500">
                                        {blockReason || 'Click below to subscribe securely.'}
                                    </div>
                                )}
                                <button
                                    onClick={() => handlePlanAction(plan)}
                                    disabled={isBusy || (current && !cancelAtPeriodEnd) || (!isCustomPlan(plan) && !current && Boolean(blockReason))}
                                    className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-center font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${plan.is_recommended && !downgrade && !current
                                        ? 'border border-transparent bg-gray-900 text-white shadow-sm hover:bg-gray-800'
                                        : 'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
                                        } ${current && !cancelAtPeriodEnd ? 'border-gray-200 bg-gray-100 text-gray-400' : ''}`}
                                >
                                    {isBusy && <Loader2 className="h-5 w-5 animate-spin" />}
                                    {isBusy ? 'Processing...' : buttonLabel}
                                </button>
                            </div>
                        </div>
                    );
                })}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full py-4 pb-20 overflow-x-hidden">
            <div className="w-full min-w-0 font-medium">

                {/* <div className="flex justify-between items-end mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Upgrade Your Plan</h2>
                        <p className="text-sm text-gray-500 mt-2">Unlock advanced AI CFO capabilities and unlimited document processing.</p>

                        {isPaid && cancelAtPeriodEnd && <span className="text-xs text-orange-600 font-medium mb-6 text-center">
                            Your plan will switch to {isDowngradingToStandard ? getPlanLabel('standard') : 'Free'} at the end of the period.
                        </span>}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                        {isPaid && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg text-sm font-semibold">
                                <Sparkles className="w-4 h-4" />
                                {isCore ? `${getPlanLabel('core')} Active` : `${getPlanLabel('standard')} Active`}
                            </div>
                        )}
                        <button
                            onClick={() => handleRefresh()}
                            disabled={isRefreshing || isEntitlementLoading}
                            className="text-sm font-semibold bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
                        >
                            {isRefreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Loader2 className="w-4 h-4" />}
                            Refresh subscription status
                        </button>
                    </div>
                </div> */}



                {activePaidPlan ? (
                    <>
                        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                            <section className="rounded-[20px] border border-gray-200 bg-white p-8">
                                <h3 className="mb-7 text-base font-semibold text-gray-900">Plan Summary</h3>
                                <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2">
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Current Plan</p>
                                        <p className="mt-1 text-sm font-medium text-gray-900">{activePlanName}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Status</p>
                                        {(cancelAtPeriodEnd || isDowngradingToStandard) && currentPeriodEnd ? (
                                            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-orange-600">
                                                <span className="h-2.5 w-2.5 rounded-full bg-orange-500" />
                                                {isDowngradingToStandard ? `Downgrading on ${currentPeriodEndLabel}` : `Canceling on ${currentPeriodEndLabel}`}
                                            </p>
                                        ) : (
                                            <p className="mt-1 flex items-center gap-1.5 text-sm font-medium text-gray-900">
                                                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                                                Active
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Start date</p>
                                        <p className="mt-1 text-sm font-medium text-gray-900">{formatDate(quota?.period_start_at)}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-500">Renews on</p>
                                        <p className="mt-1 text-sm font-medium text-gray-900">
                                            {isCancellingToFree ? 'None' : (() => {
                                                if (!quota?.period_end_at) return 'N/A';
                                                const d = new Date(quota.period_end_at);
                                                d.setDate(d.getDate() + 1);
                                                return formatDate(d.toISOString());
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            </section>

                            <section className="rounded-[20px] border border-gray-200 bg-white p-8">
                                <h3 className="mb-7 text-base font-semibold text-gray-900">Manage Billing</h3>
                                <div className="flex flex-col items-start gap-6">
                                    <button
                                        type="button"
                                        onClick={() => setIsChangePlanOpen(true)}
                                        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                                    >
                                        Change Plan
                                        <ChevronRight className="h-5 w-5" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => portalMutation.mutate()}
                                        disabled={portalMutation.isPending}
                                        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Payment Method
                                        {portalMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-5 w-5" />}
                                    </button>
                                    {/* <button
                                        type="button"
                                        onClick={() => portalMutation.mutate()}
                                        disabled={portalMutation.isPending}
                                        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Billing details
                                        {portalMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-5 w-5" />}
                                    </button> */}
                                </div>
                            </section>
                        </div>

                        <section className="mt-8 overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-white text-left text-gray-600">
                                        <tr>
                                            <th className="px-6 py-4 font-medium">Date</th>
                                            <th className="px-6 py-4 font-medium">Plan</th>
                                            <th className="px-6 py-4 font-medium">Amount</th>
                                            <th className="px-6 py-4 font-medium">Paid With</th>
                                            <th className="px-6 py-4 font-medium">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {isBillingHistoryLoading ? (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-16 text-center text-gray-500">
                                                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-indigo-600" />
                                                </td>
                                            </tr>
                                        ) : billingHistory.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-6 py-16 text-center text-gray-500">
                                                    Billing history will appear here after Stripe invoices are available.
                                                </td>
                                            </tr>
                                        ) : (
                                            billingHistory.map((row, index) => {
                                                const amount = new Intl.NumberFormat('en-US', {
                                                    style: 'currency',
                                                    currency: (row.currency || 'USD').toUpperCase(),
                                                    maximumFractionDigits: 0,
                                                }).format((row.amount_cents || 0) / 100);
                                                const status = (row.status || 'unknown').replace(/_/g, ' ');
                                                const statusClass = row.status === 'paid'
                                                    ? 'bg-emerald-50 text-emerald-700'
                                                    : row.status === 'open'
                                                        ? 'bg-yellow-50 text-yellow-700'
                                                        : row.status === 'void' || row.status === 'uncollectible'
                                                            ? 'bg-red-50 text-red-700'
                                                            : 'bg-gray-100 text-gray-600';
                                                return (
                                                    <tr key={row.invoice_id || `${row.date}-${index}`}>
                                                        <td className="px-6 py-4 text-gray-900">{formatDate(row.date || undefined)}</td>
                                                        <td className="px-6 py-4 text-gray-900">{row.plan || activePlanName}</td>
                                                        <td className="px-6 py-4 text-gray-900">{amount}</td>
                                                        <td className="px-6 py-4 text-gray-900">{row.paid_with || 'Stripe'}</td>
                                                        <td className="px-6 py-4">
                                                            <span className={`rounded px-3 py-1 text-xs font-medium capitalize ${statusClass}`}>
                                                                {status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="flex h-12 items-center justify-between border-t border-gray-200 px-6 text-xs font-medium text-gray-400">
                                <span>{billingHistory.length ? `1 - ${billingHistory.length} of ${billingHistory.length} results` : '0 results'}</span>
                                <span className="text-sm text-gray-700">Page 1 of 1</span>
                                <span className="opacity-0">0 results</span>
                            </div>
                        </section>

                    </>
                ) : (
                    <>
                        {renderPlanCards(false)}
                        {quota && (
                            <section className={`mt-8 w-full rounded-2xl border border-gray-200 bg-white p-6 shadow-sm ${inactivePlanContentWidthClass}`}>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div>
                                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Current Plan</p>
                                        <p className="font-bold text-gray-900">{getPlanLabel(effectivePlanStatus)}</p>
                                    </div>
                                    <div>
                                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</p>
                                        <p className={`flex items-center gap-1.5 text-sm font-semibold ${isTrialExpired ? 'text-gray-600' : 'text-amber-600'}`}>
                                            <span className={`h-2 w-2 rounded-full ${isTrialExpired ? 'bg-gray-400' : 'bg-amber-400'}`} />
                                            {isTrialExpired ? 'Trial Ended' : 'Trial Active'}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{isTrialExpired ? 'Trial Ended' : 'Trial Ends'}</p>
                                        <p className="font-semibold text-gray-900">{formatDate(trialEndAt || quota.period_end_at)}</p>
                                    </div>
                                </div>
                            </section>
                        )}
                    </>
                )}
            </div>

            {isChangePlanOpen && (
                <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/35 px-4 py-8">
                    <div className="relative max-h-[90vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white p-8 shadow-xl">
                        <button
                            type="button"
                            onClick={() => setIsChangePlanOpen(false)}
                            className="absolute right-4 top-4 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            aria-label="Close change plan"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <div className="mb-6 text-center">
                            <h2 className="text-lg font-semibold text-gray-900">Choose Your Monthly Subscription</h2>
                        </div>
                        {renderPlanCards(true)}
                        <div className="mt-6 flex flex-col gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
                            <button
                                type="button"
                                onClick={() => handleRefresh()}
                                disabled={isRefreshing || isEntitlementLoading}
                                className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Loader2 className="h-4 w-4" />}
                                {isRefreshing ? 'Refreshing subscription...' : 'Refresh subscription status'}
                            </button>
                            {activePaidPlan && !isCancellingToFree && (
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={isCancelling}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-gray-500 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isCancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    {isCancelling ? 'Canceling...' : 'Cancel Subscription'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
